import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { RootState } from 'store/store';
import VehicleDetails from './sub-component/VehicleDetails';
import { CommonButton, DefaultModal } from 'sharedComponents/index';
import { InputRadio } from 'sharedComponents/inputGroup/InputRadio';
import { useFormContext } from 'react-hook-form';
import { formRequiredRules } from 'helper';
import { errorMessage } from 'constants/CommonConstants';
import { Lead } from 'pages/leadGen/LeadGenInterface';
import { postLeadCreation } from 'store/LeadCreationState';
import { formatWithCommaSeparator } from 'pages/tradeIn/TradeInUtils';

const Step8: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  // Hooks
  const { t } = useTranslation();

  const { control, trigger, getValues } = useFormContext();

  const { payType, downPayment, term } = getValues();

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth);
  const widgetData = authenticateClientData?.data?.widgetData || {};

  const { offerValue } = useSelector((state: RootState) => state.commonModuleStore);

  const termPlan = authenticateClientData?.data?.widgetData?.term;
  const apr = authenticateClientData?.data?.widgetData?.apr;

  const { vehicleData, paymentData } = useSelector((state: RootState) => state.digitalRetailingStore);

  const {
    leadCreationFormData: { data: { Lead: { _id } = { _id: '' } } = { Lead: { _id: '' } } },
  } = useSelector((state: RootState) => state.leadCreationStore);

  const leadSourceId = authenticateClientData?.data?.widgetData?.leadSourceId;

  const hasCalledNextStep = React.useRef(false);

  // React Hook Form Trigger
  const nextStep = () => {
    if (_id) {
      openModal();
    } else {
      const finalData: Lead = {
        isSubmit: true,
        leadSourceId,
      };

      openModal();
      // return;
      dispatch(postLeadCreation({ data: finalData, leadTypeID: widgetData?.leadTypeId })).then((res) => {
        if (res?.payload?.success && res?.payload?.data?.Lead?.leadId) {
          openModal();
        }
      });
    }

    // onNext?.();

    // openModal();
  };

  const backStep = () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <div className='dr-widget-steps with-sidebar'>
      <div className='dr-widget-steps__content'>
        <h2 className='dr-widget-steps__title'>{t('digitalRetailing.dealSummary')}</h2>
        <p className='dr-widget-steps__step-note text-secondary'>{t('digitalRetailing.checkDetails')}</p>
        <ul className='deal-summary list-unstyled mb-0'>
          <li>
            <div className='deal-summary__lbl'>{t('digitalRetailing.vehicle')}</div>
            <div className='deal-summary__content'>{`${vehicleData?.Year} ${vehicleData?.Make} ${vehicleData?.Model} ${vehicleData?.Trim}`}</div>
          </li>
          <li>
            <div className='deal-summary__lbl'>{t('digitalRetailing.tradeIn')}</div>
            <div className='deal-summary__content'>
              {offerValue?.Valued ? `$${formatWithCommaSeparator(offerValue?.Value)}` : 'Unavailable'}
            </div>
          </li>
          {/* <li>
            <div className='deal-summary__lbl'>{t('digitalRetailing.incentives')}</div>
            <div className='deal-summary__content'>N/A</div>
          </li> */}
          <li>
            <div className='deal-summary__lbl'>{t('digitalRetailing.paymentType')}</div>
            <div className='deal-summary__content'>
              {payType}
              {downPayment ? (
                <>
                  <br />
                  {`${t('digitalRetailing.downPayment')}  : $${formatWithCommaSeparator(downPayment)}`}
                </>
              ) : null}

              {apr ? (
                <>
                  <br />
                  {`${t('digitalRetailing.apr')}  : ${apr}%`}
                </>
              ) : null}

              {term || termPlan ? (
                <>
                  <br />
                  {`${t('digitalRetailing.term')}  : ${term || termPlan} ${t('digitalRetailing.months')}`}
                </>
              ) : null}

              {paymentData?.monthly ? (
                <>
                  <br />
                  {`${t('digitalRetailing.monthlyPayment')}  : $${paymentData?.monthly?.toFixed(2)}`}
                </>
              ) : null}
            </div>
          </li>
          {/* <li>
            <div className='deal-summary__lbl'>Protection</div>
            <div className='deal-summary__content'>
              Tire & Rim Protection
              <br />
              36 months
            </div>
          </li> */}
        </ul>
      </div>

      <VehicleDetails />

      {/* Modal */}

      <DefaultModal
        open={isModalOpen}
        handleClose={closeModal}
        rootClass='blur-backdrop'
        customClass='pre-approval-modal'
        childern={
          <div className='modal-body'>
            <h2 className='pre-approval-modal__title'>{t('digitalRetailing.whatWouldYouLikeToDo')}</h2>
            <div className='dr-radio-style'>
              <InputRadio
                name={'nextAction'}
                control={control}
                options={[
                  { label: t('digitalRetailing.getPreApprovedNow'), value: 'preApproved' },
                  { label: t('digitalRetailing.reserveAndScheduleTestDrive'), value: 'scheduleTestDrive' },
                ]}
                triggerValidationForField={trigger}
                rules={formRequiredRules(errorMessage(t).radioError)}
              />
            </div>
            <CommonButton
              label={t('digitalRetailing.proceed')}
              onClick={() => {
                {
                  if (hasCalledNextStep.current) return;
                  hasCalledNextStep.current = true;

                  const nextAction = getValues('nextAction');
                  nextAction === 'preApproved' ? onNext?.() : onNext?.(11);
                }
              }}
            />
          </div>
        }
      />
    </div>
  );
};

const StepperForm8 = React.forwardRef(Step8);

export default StepperForm8;
